/**
 * Brand identity.
 *
 * The word BUILT is the fixed anchor across all three experiences; only the
 * qualifier changes. Components render `pathway.wordmark.anchor` and
 * `pathway.wordmark.qualifier` separately so the anchor can stay visually
 * constant through route transitions.
 */

import { placeholder } from './placeholders.js';

export type PathwayId = 'master' | 'personal-training' | 'boxing';

/** Master colour system, shared by all three experiences. */
export const palette = {
  nearBlack: '#080A0D',
  graphite: '#22272E',
  warmWhite: '#F3F0E9',
  coolSteel: '#8D97A4',
  platinum: '#C7CDD5',
  smokedGlass: 'rgba(255,255,255,0.06)',

  /** Personal training. */
  electricBlue: '#168CFF',
  /** Reserved for positive progress, completion and confirmation only. */
  performanceGreen: '#A8E65C',

  /** Boxing. Used selectively - never as a blanket surface colour. */
  controlledRed: '#E3373D',
  deepCrimson: '#8D1D29',
} as const;

export interface PathwayIdentity {
  id: PathwayId;
  /** Full brand line, e.g. `BUILT WITH PURPOSE`. */
  wordmark: { anchor: 'BUILT'; qualifier: string };
  /** Title-case display name, e.g. `Built With Purpose`. */
  label: string;
  /** One-line positioning statement. */
  tagline: string;
  /** Longer supporting statement used beneath hero headlines. */
  supporting: string;
  /** Accent colour token name resolved by the Tailwind theme. */
  accent: string;
  accentHex: string;
  /** Emotional register, documented so design changes stay on-brief. */
  register: readonly string[];
}

export const pathways: Record<PathwayId, PathwayIdentity> = {
  master: {
    id: 'master',
    wordmark: { anchor: 'BUILT', qualifier: 'WITH PURPOSE' },
    label: 'Built With Purpose',
    tagline: 'Strength. Skill. Confidence. Built Around You.',
    supporting:
      'Personal training, nutrition, conditioning and boxing coaching designed around the individual.',
    accent: 'accent-master',
    accentHex: palette.warmWhite,
    register: ['neutral', 'premium', 'cinematic', 'strategic', 'human', 'performance-led'],
  },
  'personal-training': {
    id: 'personal-training',
    wordmark: { anchor: 'BUILT', qualifier: 'BY DESIGN' },
    label: 'Built by Design',
    tagline: 'Fuel With Intent. Train With Structure. Progress With Purpose.',
    supporting:
      'Personalised coaching that combines nutrition, resistance training and cardiovascular conditioning through one structured system.',
    accent: 'accent-pt',
    accentHex: palette.electricBlue,
    register: ['sleek', 'precise', 'technical', 'structured', 'data-informed', 'clean'],
  },
  boxing: {
    id: 'boxing',
    wordmark: { anchor: 'BUILT', qualifier: 'IN ROUNDS' },
    label: 'Built in Rounds',
    tagline: 'Train With Purpose. Fight With Confidence.',
    supporting:
      'Dedicated boxing coaching for technique, conditioning, movement and confidence.',
    accent: 'accent-boxing',
    accentHex: palette.controlledRed,
    register: ['darker', 'cinematic', 'physical', 'tactile', 'controlled'],
  },
};

export const brand = {
  businessName: 'Iron Resolve Combat Fitness',
  /** The overarching brand promise. */
  masterBrandLine: 'BUILT WITH PURPOSE',
  /** Core message, used in the master hero and in the pathway split. */
  coreMessage: 'One coach. One standard. Two distinct ways to train.',

  logoPath: '/assets/brand/logo-primary.png',
  monogramPath: placeholder('MONOGRAM PATH'),
  /** Letterform fallback rendered when no monogram artwork exists yet. */
  monogram: placeholder('COACH MONOGRAM'),

  palette,
  pathways,

  typography: {
    /** Master + personal training: clean geometric sans-serif. */
    sans: "'Space Grotesk Variable', 'Space Grotesk', 'Inter', system-ui, sans-serif",
    /** Boxing display: condensed, vertical, forceful, still premium. */
    condensed: "'Barlow Condensed', 'Space Grotesk', system-ui, sans-serif",
    /** Numeric/technical annotations in 3D scene labels and data readouts. */
    mono: "'IBM Plex Mono', ui-monospace, SFMono-Regular, monospace",
  },
} as const;

/** Resolves a pathway identity, defaulting to the master brand. */
export function pathwayFor(id: PathwayId | null | undefined): PathwayIdentity {
  return pathways[id ?? 'master'];
}
